"use client"

import { HandCoins, type LucideIcon } from "lucide-react"
import { useCallback } from "react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { NavigationProps, AppPage } from "@/types/navigation"
import { URL_TO_PAGE_MAP } from "@/routes/routes"

// --- Mock Collaborator Data for Demo ---
interface Collaborator {
  id: string;
  name: string;
  initials: string;
  imageUrl: string | null;
}

const mockCollaborators: Collaborator[] = [
  { id: 'user1', name: 'Me', initials: 'ME', imageUrl: 'https://images.steamusercontent.com/ugc/937185524768962464/98DD1D8BB7235A25054111C9436A9E11BF47F356/?imw=512&&ima=fit&impolicy=Letterbox&imcolor=%23000000&letterbox=false' }, // Using the steam image for demo
  { id: 'user2', name: 'Jane Doe', initials: 'JD', imageUrl: 'https://i.pravatar.cc/150?u=user2alt' },
  { id: 'user3', name: 'Peter Pan', initials: 'PP', imageUrl: 'https://i.pravatar.cc/150?u=user3alt' },
];
// --- End Mock Data ---

// Define budget-specific items with collaborators
const budgetItems = [
  {
    title: "Needs",
    url: "/budgets/overview",
    icon: "🏠",
    collaboratorIds: ['user1', 'user2'], // Shared budget
  },
  {
    title: "Wants",
    url: "/budgets/wants",
    icon: "🛍️",
    collaboratorIds: ['user1'], // Only me
  },
  {
    title: "Travel",
    url: "/budgets/travel",
    icon: "✈️",
    collaboratorIds: ['user1', 'user2', 'user3'], // Shared with multiple
  },
  {
    title: "Lovely Mustang 69",
    url: "/budgets/mustang",
    icon: "🚗",
    collaboratorIds: ['user1'], // Only me
  },
  {
    title: "Savings",
    url: "/budgets/savings",
    icon: "💰",
    collaboratorIds: ['user1', 'user2'], // Shared
  },
]

export function NavBudgets({
  onNavigate,
  currentPage,
  sidebarState,
}: {
  currentPage?: AppPage
  sidebarState?: "expanded" | "collapsed"
} & NavigationProps) {
  // Handle navigation for sidebar items
  const handleNavigation = useCallback((url: string) => {
    const page = URL_TO_PAGE_MAP[url]
    if (page) {
      onNavigate(page)
      return true
    }
    console.warn(`No page mapping found for URL: ${url}`)
    return false
  }, [onNavigate])

  // Map items with active state and collaborator details
  const itemsWithDetails = budgetItems.map(item => {
    const collaborators = item.collaboratorIds
      .map(id => mockCollaborators.find(c => c.id === id))
      .filter((c): c is Collaborator => !!c); // Find collaborator objects and filter out undefined

    return {
      ...item,
      isActive: currentPage === URL_TO_PAGE_MAP[item.url],
      collaborators: collaborators,
    };
  });

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="flex items-center justify-between">
        <button
          onClick={() => handleNavigation('/budgets')}
          className="flex-grow text-left px-1 font-medium hover:text-foreground focus:outline-none"
        >
          Budgets
        </button>
        <button
          className="text-lg opacity-50 hover:opacity-100 px-1 flex-shrink-0"
          onClick={(e) => { e.stopPropagation(); /* Placeholder action */ console.log("Add budget clicked"); }}
          aria-label="Add new budget"
          title="Add new budget"
        >
          +
        </button>
      </SidebarGroupLabel>
      <SidebarMenu>
        {itemsWithDetails.map((item) => (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton
              tooltip={item.title}
              onClick={() => handleNavigation(item.url)}
              className="relative flex justify-between items-center w-full" // Use flex to position avatars
              isActive={item.isActive}
            >
              {/* Left side: Icon and Title */}
              <div className="flex items-center gap-2 overflow-hidden">
                {item.icon && <span className="">{item.icon}</span>}
                <span className="truncate">{item.title}</span>
              </div>

              {/* Right side: Collaborator Avatars */}
              {sidebarState === "expanded" && item.collaborators.length > 0 && (
                 <div className="flex items-center -space-x-2 flex-shrink-0 ml-2">
                  {item.collaborators.slice(0, 3).map((collab) => ( // Limit to 3 avatars for space
                    <Avatar key={collab.id} className="h-5 w-5 border-2 border-background group-hover:border-sidebar-accent transition-colors duration-100">
                      <AvatarImage src={collab.imageUrl ?? undefined} alt={collab.name} />
                      <AvatarFallback className="text-[8px]">
                        {collab.initials}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {item.collaborators.length > 3 && ( // Show +N if more than 3
                     <div className="flex items-center justify-center h-5 w-5 rounded-full bg-muted text-muted-foreground text-[8px] border-2 border-background group-hover:border-sidebar-accent transition-colors duration-100 z-10">
                       +{item.collaborators.length - 3}
                     </div>
                   )}
                </div>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
} 